import { useState } from "react";
import styles from "./BookingForm.module.css";
import { SERVICE_OPTIONS } from "../data/services";
import { createBooking } from "../services/bookingService";
import { LeafIcon } from "./icons";

const emptyForm = (initialService) => ({
  name: "",
  phone: "",
  email: "",
  service: initialService || "",
  preferredDate: "",
  address: "",
  notes: "",
});

function BookingForm({ initialService = "" }) {
  const [form, setForm] = useState(emptyForm(initialService));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Let us know your name.";
    if (!form.phone.trim()) next.phone = "A phone number so we can confirm.";
    if (!form.service) next.service = "Pick a service.";
    if (!form.preferredDate) next.preferredDate = "Pick a preferred date.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!validate()) return;

    setSubmitting(true);
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), 12000)
      );
      await Promise.race([createBooking(form), timeout]);
      setSuccess(true);
    } catch (err) {
      setFormError(
        "Couldn't confirm that went through — please call or text us directly to be safe."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className={`${styles.form} ${styles.successCard}`}>
        <LeafIcon className={styles.successIcon} />
        <h3>Booking request sent</h3>
        <p>
          Thanks {form.name.split(" ")[0] || "there"} — we'll be in touch at{" "}
          {form.phone} to confirm your {"  "}
          {SERVICE_OPTIONS.find((s) => s.value === form.service)?.label.toLowerCase() ||
            "booking"}
          .
        </p>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.grid}>
        <div className={styles.row2}>
          <div className={styles.field}>
            <label htmlFor="name">
              Name <span className={styles.required}>*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              autoComplete="name"
            />
            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="phone">
              Phone <span className={styles.required}>*</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              autoComplete="tel"
            />
            {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="email">Email (optional)</label>
          <input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
          />
        </div>

        <div className={styles.row2}>
          <div className={styles.field}>
            <label htmlFor="service">
              Service <span className={styles.required}>*</span>
            </label>
            <select
              id="service"
              name="service"
              value={form.service}
              onChange={handleChange}
            >
              <option value="" disabled>
                Choose a service
              </option>
              {SERVICE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                  {s.priceLabel ? ` — ${s.priceLabel}` : ""}
                </option>
              ))}
            </select>
            {errors.service && (
              <span className={styles.errorText}>{errors.service}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="preferredDate">
              Preferred date <span className={styles.required}>*</span>
            </label>
            <input
              id="preferredDate"
              name="preferredDate"
              type="date"
              value={form.preferredDate}
              onChange={handleChange}
            />
            {errors.preferredDate && (
              <span className={styles.errorText}>{errors.preferredDate}</span>
            )}
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="address">Address</label>
          <input
            id="address"
            name="address"
            type="text"
            value={form.address}
            onChange={handleChange}
            placeholder="Street, Kerikeri"
            autoComplete="street-address"
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="notes">Anything we should know?</label>
          <textarea
            id="notes"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            placeholder="Gate code, size of section, car make/model, etc."
          />
        </div>
      </div>

      <div className={styles.submitRow}>
        <button type="submit" className="btn btnPrimary" disabled={submitting}>
          {submitting ? "Sending…" : "Send booking request"}
        </button>
        {formError && <span className={styles.formError}>{formError}</span>}
      </div>
    </form>
  );
}

export default BookingForm;
